import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import Nav from "@/sections/Nav";
import Hero from "@/sections/Hero";
import Marquee from "@/sections/Marquee";
import HowToBake from "@/sections/HowToBake";
import Manifesto from "@/sections/Manifesto";
import Tips from "@/sections/Tips";
import Reviews from "@/sections/Reviews";
import Faq from "@/sections/Faq";
import Footer from "@/sections/Footer";
import OurStory from "@/pages/OurStory";
import OurMixes from "@/pages/OurMixes";
import { CartProvider } from "@/context/CartContext";
import CartDrawer from "@/components/CartDrawer";

function Home() {
  useEffect(() => {
    if (window.location.hash) {
      const t = setTimeout(() => {
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <main>
      <Hero />
      <Marquee />
      <HowToBake />
      <Manifesto />
      <Tips />
      <Reviews />
      <Faq />
    </main>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    const onClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const target = document.querySelector(anchor.getAttribute("href"));
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -112 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <BrowserRouter>
      <CartProvider>
        <div className="bg-[#FAF7F2] min-h-screen">
          <div className="grain-overlay" aria-hidden="true" />
          <Nav />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/our-story" element={<OurStory />} />
            <Route path="/mixes" element={<OurMixes />} />
          </Routes>
          <Footer />
          <a
            href="https://wa.me/918886016001?text=Hi%20Almo%20Bakes!%20I%20have%20a%20question%20about%20your%20mixes."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-float-button"
            aria-label="Chat with Almo Bakes on WhatsApp"
            className="fixed bottom-5 right-5 z-[65] group flex items-center gap-0 rounded-full bg-[#25D366] text-white shadow-xl shadow-[#25D366]/30 hover:shadow-2xl hover:scale-[1.05] active:scale-[0.97] transition-all duration-300 p-4"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span className="max-w-0 overflow-hidden group-hover:max-w-[120px] group-hover:pl-2.5 whitespace-nowrap text-sm font-medium transition-all duration-300">
              Chat with us
            </span>
          </a>
          <CartDrawer />
          <Toaster position="bottom-right" toastOptions={{ style: { background: "#2C221E", color: "#FAF7F2", border: "1px solid rgba(250,247,242,0.15)", borderRadius: "1rem" } }} />
        </div>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
